/**
 * {project}の画面のタブ「見積依頼」（#119 docs/56 §6.5 → #218 docs/88 §4.1・Figma ①）。使う機能 rfq の会社だけ。
 *   上：状態のチップの絞り込み（すべて・下書き・未回答・回答あり・取消。数つき）と、右上の「見積依頼書作成」（見積が無くても押せる）
 *   表：番号・{vendor}・件名・状態・送付日・回答期限・明細（n行／見積 n・追加 n）・添付（図面 n・写真 n）・回答・⋯
 *     回答あり＝{vendorQuote}の番号と金額と「見積で取込 ›」、取消の行は薄く「RQ-0004 で再依頼」
 *   説明の文（14 日で朱・明細は控え）は画面に並べず「？」へ（CLAUDE.md）
 *   スマホ（幅 767px 以下・スマホ版）：一覧だけ（作るのは PC）。表はカード
 *   #179：届く前もチップの段と表の場所を同じ形で取る（数は「…」）
 */
import { ORDER_BUNDLE_MSG, RFQ_MSG, RFQ_STATUS_LABEL, pickNameMapOf, pickNamesOf, type GroupList, type PartnerList, type Rfq, type RfqList, type RfqListFilter, type RfqSummary } from "@genbaryoku/contract";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterInput } from "../../components/ListHead.jsx";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { DOTS_H } from "../../components/HeadActions.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { PartnerField } from "../../components/PartnerPickerDrawer.jsx";
import { RfqPurgeDialog } from "./PurgeDialog.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { answerAmount, attachCell, linesCell, md, overdueText } from "./parts.jsx";
import { markNewRfq } from "./sendPrefs.js";
import { useMe } from "../../auth.js";
import { notify } from "../../components/Notice.jsx";
import { usePcOnly } from "../../lib/pcOnly.js";
import { BundleBar, OrderedMark, SelectBox, useOrderBundle } from "../orders/bundle.jsx";

const FILTERS: Array<[RfqListFilter, string]> = [
  ["all", RFQ_MSG.tabAll],
  ["draft", RFQ_MSG.tabDraft],
  ["waiting", RFQ_MSG.tabWaiting],
  ["answered", RFQ_MSG.tabAnswered],
  ["ordered", ORDER_BUNDLE_MSG.filterOrdered],
  ["cancelled", RFQ_MSG.tabCancelled],
];

/**
 * 見積依頼の行の「⋯」（{project}のタブと見積依頼の一覧で同じ項目）：開く・別の{vendor}へ複製・PDF。
 * #275（docs/99 §5）：取消した依頼はいちばん下に「削除」（朱）＝完全に削除（見積依頼書の画面の「⋯」と同じ窓 RfqPurgeDialog）
 */
export function rfqRowMenu(r: RfqSummary, prefix: string, onPurge: () => void): RowMenuItem[] {
  return [
    { label: RFQ_MSG.rowOpen, to: `/rfqs/${r.id}`, testId: `${prefix}-open` },
    { label: t(RFQ_MSG.copy), to: `/rfqs/${r.id}?copy=1`, testId: `${prefix}-copy` },
    r.status === "draft"
      ? { label: RFQ_MSG.pdfDraft, to: `/rfqs/${r.id}/paper`, testId: `${prefix}-pdf` }
      : { label: RFQ_MSG.pdfOpen, to: `/rfqs/${r.id}/paper`, testId: `${prefix}-pdf` },
    ...(r.status === "cancelled" ? [{ label: RFQ_MSG.purge, danger: true, divider: true, testId: `${prefix}-purge`, onSelect: onPurge }] : []),
  ];
}

export function GroupRfqs({ groupId, showEmpty = false }: { groupId: string; /** 0件でも「見積依頼はありません」を出す */ showEmpty?: boolean }) {
  const L = useLabels();
  const nav = useNavigate();
  const narrow = useNarrow();
  const [f, setF] = useState<RfqListFilter>("all");
  const [create, setCreate] = useState(false);
  const q = useQuery({ queryKey: ["rfqs", "group", groupId, f], queryFn: () => api<RfqList>("GET", `/rfqs?f=${f}&g=${groupId}`), placeholderData: (prev) => prev });
  const items = q.data?.items ?? [];
  const counts = q.data?.counts;
  /** {vendor}の名前：略称。同じ略称の別の会社が並ぶときだけ「略称（正式名）」 */
  const names = pickNameMapOf(items.map((r) => ({ id: r.vendor.id, short: r.vendor.shortName, full: r.vendor.name })));
  const vname = (r: RfqSummary) => names.get(r.vendor.id) ?? r.vendor.shortName;
  const open = (r: RfqSummary) => nav(`/rfqs/${r.id}`);
  // #233 2a：行を選ぶ（PC・モジュール sales）→ 帯で「まとめて作成」（1件は「{order}作成」）。送付した依頼だけ
  const me = useMe();
  const pc = usePcOnly();
  const canBundle = !narrow && !pc.phone && !!me.data?.tenant.modules.includes("sales") && !pc.hidden("/orders/new");
  const [picked, setPicked] = useState<Map<string, RfqSummary>>(new Map());
  useEffect(() => setPicked(new Map()), [f]);
  const bundle = useOrderBundle();
  const pickable = (r: RfqSummary) => r.status === "sent" || r.status === "answered";
  const [purge, setPurge] = useState<RfqSummary | null>(null);
  const rowMenu = (r: RfqSummary): RowMenuItem[] => rfqRowMenu(r, "group-rfq", () => setPurge(r));
  if (!showEmpty && q.data && !q.data.counts.all) return null;
  return (
    <section className="grfqs" data-testid="group-rfqs">
      <div className="grfq-tools">
        <div className="grfq-chips" role="group" aria-label={RFQ_MSG.colStatus} data-testid="group-rfq-chips">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" className={"chip grfq-chip" + (f === k ? " is-on" : "")} aria-pressed={f === k} onClick={() => setF(k)} data-testid={`group-rfq-chip-${k}`}>
              {label} <span className="num">{counts ? counts[k] : "…"}</span>
            </button>
          ))}
        </div>
        <Help label={RFQ_MSG.listTitle} text={RFQ_MSG.listHelp} testId="help-group-rfqs" />
        <span className="sp" />
        {narrow ? null : (
          <button type="button" className="btn primary" onClick={() => setCreate(true)} data-testid="group-rfq-create">
            {RFQ_MSG.createTitle}
          </button>
        )}
      </div>
      {q.data && !items.length ? (
        <p className="hint grfq-empty" data-testid="group-rfqs-empty">{RFQ_MSG.listEmpty}</p>
      ) : narrow ? (
        <ul className="gcards grfq-cards" data-testid="group-rfq-cards">
          {items.map((r) => (
            <li key={r.id} className={r.status === "cancelled" ? "is-quiet" : undefined}>
              <button type="button" className="gcard" onClick={() => open(r)} data-testid="group-rfq-row">
                <span className="grfq-cardtop">
                  <b className="num">{r.no ?? RFQ_MSG.draftNo}</b>
                  <span className="chip" data-status={r.status}>{RFQ_STATUS_LABEL[r.status]}</span>
                </span>
                <span>{vname(r)}　{r.subject}</span>
                <span className="muted">{linesCell(r).top}{r.overdueDays ? <b className="shu">　{overdueText(r)}</b> : null}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="grfq-tablewrap">
          {canBundle ? (
            <BundleBar
              count={picked.size}
              busy={bundle.busy}
              onClear={() => setPicked(new Map())}
              onCreate={() => {
                const rs = [...picked.values()];
                bundle.start({ rfqIds: rs.map((r) => r.id) }, rs.flatMap((r) => r.orders)).catch((e) => notify({ text: messageOf(e), tone: "error" }));
              }}
            />
          ) : null}
          <table className="list grfq-table" aria-busy={!q.data || undefined}>
            <thead>
              <tr>
                {canBundle ? <th aria-label={ORDER_BUNDLE_MSG.selectRow} className="obsel-cell" /> : null}
                <th>{RFQ_MSG.colNo}</th>
                <th>{L.vendor}</th>
                <th>{RFQ_MSG.colSubject}</th>
                <th>{RFQ_MSG.colStatus}</th>
                <th>{RFQ_MSG.colSentAt}</th>
                <th>{RFQ_MSG.colReplyBy}</th>
                <th>{RFQ_MSG.colLines}</th>
                <th>{RFQ_MSG.colAttach}</th>
                <th>{RFQ_MSG.colAnswer}</th>
                <th aria-label="操作" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const lc = linesCell(r);
                return (
                  <tr
                    key={r.id}
                    className={"r" + (r.status === "cancelled" ? " is-quiet" : "")}
                    tabIndex={0}
                    onClick={(e) => !(e.target as HTMLElement).closest("a,button,input") && open(r)}
                    onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(r)}
                    data-testid="group-rfq-row"
                  >
                    {canBundle ? (
                      <td className="obsel-cell">
                        <SelectBox
                          on={picked.has(r.id)}
                          label={`${r.no ?? RFQ_MSG.draftNo} ${vname(r)}`}
                          disabled={!pickable(r)}
                          onChange={(on) => setPicked((m) => {
                            const n = new Map(m);
                            if (on) n.set(r.id, r);
                            else n.delete(r.id);
                            return n;
                          })}
                        />
                      </td>
                    ) : null}
                    <td className="num">{r.no ?? <span className="muted">{RFQ_MSG.draftNo}</span>}</td>
                    <td title={r.vendor.name} data-testid="group-rfq-vendor">{vname(r)}</td>
                    <td className="grfq-subj" title={r.subject}>{r.subject}</td>
                    <td>
                      <span className="chip grfq-st" data-status={r.status}>{r.status === "answered" ? "✓ " : ""}{RFQ_STATUS_LABEL[r.status]}</span>
                      <OrderedMark refs={r.orders} testId="group-rfq-ordered" />
                    </td>
                    <td className="num">{r.sentAt ? md(r.sentAt.slice(0, 10)) : "—"}</td>
                    <td className="num">{r.replyBy ? md(r.replyBy) : "—"}</td>
                    <td data-testid="group-rfq-lines">
                      <span className="grfq-two"><span>{lc.top}</span><small className="muted">{lc.sub}</small></span>
                    </td>
                    <td data-testid="group-rfq-attach">{attachCell(r)}</td>
                    <td data-testid="group-rfq-answer">
                      {r.status === "answered" && r.answer ? (
                        <span className="grfq-two">
                          <span className="num">{`${r.answer.quoteNo}　${answerAmount(r)}`.trim()}</span>
                          <Link to={`/vendor-quotes/${r.answer.vendorQuoteId}`} data-testid="group-rfq-import">{RFQ_MSG.importAnswer} ›</Link>
                        </span>
                      ) : r.status === "answered" ? (
                        RFQ_MSG.tabAnswered
                      ) : r.status === "draft" ? (
                        <span className="muted">{RFQ_MSG.notSent}</span>
                      ) : r.status === "cancelled" ? (
                        r.redoneBy ? <Link to={`/rfqs/${r.redoneBy.id}`} data-testid="group-rfq-redone">{RFQ_MSG.redone.replace("{no}", r.redoneBy.no ?? RFQ_MSG.draftNo)}</Link> : <span className="muted">—</span>
                      ) : r.supersededBy ? (
                        <span className="muted">{RFQ_MSG.supersededBy.replace("{no}", r.supersededBy)}</span>
                      ) : r.overdueDays ? (
                        <b className="shu" data-testid="rfq-overdue">{overdueText(r)}</b>
                      ) : (
                        RFQ_MSG.noAnswer
                      )}
                    </td>
                    <td className="grfq-more">
                      <RowMenu label={RFQ_MSG.lineMenu.replace("{name}", r.no ?? r.subject)} items={rowMenu(r)} testId="group-rfq-more" trigger={DOTS_H} triggerClass="rowmore dots" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {create ? <CreateRfqDialog groupId={groupId} onClose={() => setCreate(false)} /> : null}
      {purge ? <RfqPurgeDialog rfq={purge} onClose={() => setPurge(null)} /> : null}
      {bundle.dialog}
    </section>
  );
}

/**
 * 窓「見積依頼書作成」：{vendor}を選ぶ（その場で登録も）→「作成」で下書きを作って作成画面へ（脇は「取消」）
 * 見積書の画面の「⋯」→「見積依頼書作成…」は見積の付いた依頼（EstimateRfq.tsx の窓）、ここは{project}の入口（明細は空）
 * 見積依頼の一覧の「＋ 見積依頼書作成」（#241・★5）は pickGroup：先に{project}を選ぶ（最近動いた{project}・絞り込み）。
 * 見積から開いた一覧なら、その見積の{project}を選んだ形で始める（initialGroup）
 */
export function CreateRfqDialog({
  groupId: fixedGroup,
  onClose,
  copyOf,
  pickGroup = false,
  initialGroup,
}: {
  groupId?: string;
  onClose: () => void;
  /** 別の{vendor}へ複製（作成画面の「⋯」） */ copyOf?: Rfq;
  /** {project}を窓の中で選ぶ（見積依頼の一覧の入口） */ pickGroup?: boolean;
  initialGroup?: { id: string; name: string } | null;
}) {
  const L = useLabels();
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(initialGroup ?? null);
  useEffect(() => {
    if (initialGroup) setPicked((g) => g ?? initialGroup);
  }, [initialGroup]);
  const groupId = pickGroup ? picked?.id : fixedGroup;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  // {vendor}は取引先検索（#288、docs/101 §4。仕入先が既定・新規登録もドロアーの中）
  const [vendor, setVendor] = useState("");
  const [vendorName, setVendorName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const title = copyOf ? t(RFQ_MSG.copyTitle) : RFQ_MSG.createTitle;
  const go = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const vid = vendor;
      if (!vid) return;
      const r = copyOf ? await api<Rfq>("POST", `/rfqs/${copyOf.id}/copy`, { vendorId: vid }) : await api<Rfq>("POST", `/groups/${groupId}/rfqs`, { vendorId: vid });
      void qc.invalidateQueries({ queryKey: ["rfqs"] });
      if (copyOf) toast(t(RFQ_MSG.copied));
      else markNewRfq(r.id); // 新しく作った下書き＝「次回も同じ設定」を当てる（複製は元の設定のまま）
      nav(`/rfqs/${r.id}`);
      onClose();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form
        className="dlg grfq-dlg"
        role="dialog"
        aria-label={title}
        onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}
        onSubmit={(e) => {
          e.preventDefault();
          void go();
        }}
        data-testid="rfq-create-dialog"
      >
        <div className="dh">{title}</div>
        <div className="db">
          {pickGroup ? <GroupPick value={picked} onChange={setPicked} /> : null}
          <PartnerField
            role="vendor"
            label={L.vendor}
            value={vendorName ? { name: vendorName } : null}
            autoFocus={!pickGroup}
            onPick={(c) => {
              if (copyOf && c.id === copyOf.vendor.id) {
                setErr(t(RFQ_MSG.copySameVendor));
                return;
              }
              setVendor(c.id);
              setVendorName(c.displayName);
              setErr(undefined);
            }}
            testId="rfq-create-vendor"
          />
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button type="submit" className="btn primary" disabled={busy || (pickGroup && !picked) || !vendor} data-testid="rfq-create-do">
            {copyOf ? RFQ_MSG.copyDo : RFQ_MSG.createDo}
          </button>
        </div>
      </form>
    </div>
  );
}

/** {project}を選ぶ（見積依頼の一覧の「＋ 見積依頼書作成」の窓。#241・★5）：最近動いた{project}を 8件、絞り込みで探す */
function GroupPick({ value, onChange }: { value: { id: string; name: string } | null; onChange: (g: { id: string; name: string }) => void }) {
  const L = useLabels();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const list = useQuery({
    queryKey: ["group-list", "active", "", qApplied],
    queryFn: () => api<GroupList>("GET", `/groups?state=active${qApplied ? `&q=${encodeURIComponent(qApplied)}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const items = (list.data?.items ?? []).slice(0, 8);
  const shown = value && !items.some((g) => g.id === value.id) ? [{ id: value.id, name: value.name, client: null as null | { name: string } }, ...items] : items;
  return (
    <div className="grfq-gpick" data-testid="rfq-create-group">
      <span className="grfq-gpick-l">{L.project}</span>
      <FilterInput value={q} onChange={setQ} placeholder={`${L.project}名・${L.customer}名で絞り込み`} testId="rfq-create-group-q" />
      <div className="grfq-gpick-list" role="radiogroup" aria-label={L.project}>
        {shown.map((g) => (
          <button key={g.id} type="button" role="radio" aria-checked={value?.id === g.id} className={"grfq-gpick-row" + (value?.id === g.id ? " is-on" : "")} onClick={() => onChange({ id: g.id, name: g.name })}>
            <b>{g.name}</b>
            {g.client ? <span className="muted">{g.client.name}</span> : null}
          </button>
        ))}
        {list.data && !shown.length ? <p className="hint">{`該当する${L.project}はありません`}</p> : null}
      </div>
    </div>
  );
}
